import IOSelect from './ui/IOSelect';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button, Input, Label, Card, CardHeader, CardTitle, CardContent, Alert, AlertDescription } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { roundHourlyRate } from '../utils/formatters';
import axios from '../api/axios';
import { Sun, Moon } from 'lucide-react';

// Lazy-load heavy sub-pages (only used on click)
const PasswordReset = lazy(() => import('./PasswordReset'));
const DatenschutzPage = lazy(() => import('./DatenschutzPage'));

const ThemeOrb = () => {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';
  return (
    <button
      onClick={toggleTheme}
      aria-label={isLight ? 'Dunkelmodus aktivieren' : 'Hellmodus aktivieren'}
      title={isLight ? 'Dunkelmodus' : 'Hellmodus'}
      className={`fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
        isLight
          ? 'bg-white text-blue-600 hover:bg-gray-50 shadow-sm'
          : 'bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 text-yellow-400 hover:border-gray-600'
      }`}
      data-testid="auth-theme-toggle"
    >
      {isLight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
    </button>
  );
};

// „Mehr Infos"-Button (bottom-left) — führt zur Marketing-Landing-Page.
// Wenn onOpenLanding gesetzt ist, wird der Klick in-app abgefangen und
// die Slide-Animation im `PublicShell` läuft; sonst Full-Page-Load.
const MoreInfoLink = ({ onOpenLanding }) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const handleClick = (e) => {
    if (onOpenLanding) {
      e.preventDefault();
      onOpenLanding();
    }
  };
  return (
    <a
      href="/mehr-infos"
      onClick={handleClick}
      className={`fixed bottom-6 left-6 z-50 inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-medium transition-all ${
        isLight
          ? 'bg-white text-blue-600 hover:bg-gray-50 shadow-sm'
          : 'bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 text-yellow-400 hover:border-gray-600'
      }`}
      data-testid="auth-more-info-link"
    >
      Mehr Infos
    </a>
  );
};

const LanguageOrb = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const orbRef = useRef(null);

  const languages = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orbRef.current && !orbRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setIsOpen(false);
  };

  const currentLabel = languages.find(l => i18n.language?.startsWith(l.code))?.label || 'Deutsch';

  return (
    <div ref={orbRef} className="fixed top-6 right-6 z-50" data-testid="language-orb">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className={`w-full flex items-center justify-center px-3 py-1.5 rounded-md text-xs transition-all ${
          isLight
            ? 'bg-white text-blue-600 hover:bg-gray-50 shadow-sm'
            : 'bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 text-gray-300 hover:text-yellow-400 hover:border-gray-600'
        }`}
        data-testid="language-orb-toggle"
      >
        <span>{currentLabel}</span>
      </button>

      {isOpen && (
        <div className={`mt-0.5 rounded-md overflow-hidden whitespace-nowrap ${
          isLight
            ? 'bg-white shadow-sm'
            : 'bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 shadow-lg'
        }`}>
          {languages.map(lang => {
            const isActive = i18n.language?.startsWith(lang.code);
            return (
              <button
                key={lang.code}
                onClick={() => handleChange(lang.code)}
                className={`block w-full text-center px-3 py-1.5 text-xs transition-colors ${
                  isLight
                    ? isActive
                      ? 'text-blue-600 font-medium bg-blue-50'
                      : 'text-gray-500 hover:text-blue-600 hover:bg-gray-50'
                    : isActive
                      ? 'text-yellow-400 bg-gray-700/50'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
                }`}
                data-testid={`language-orb-${lang.code}`}
              >
                {lang.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Country codes for phone prefix selector (names are translation keys)
const countryCodes = [
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+7', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: '+20', flag: '\u{1F1EA}\u{1F1EC}' },
  { code: '+27', flag: '\u{1F1FF}\u{1F1E6}' },
  { code: '+30', flag: '\u{1F1EC}\u{1F1F7}' },
  { code: '+31', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: '+32', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: '+33', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+34', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: '+36', flag: '\u{1F1ED}\u{1F1FA}' },
  { code: '+39', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: '+40', flag: '\u{1F1F7}\u{1F1F4}' },
  { code: '+41', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: '+43', flag: '\u{1F1E6}\u{1F1F9}' },
  { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+45', flag: '\u{1F1E9}\u{1F1F0}' },
  { code: '+46', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: '+47', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: '+48', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: '+49', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+51', flag: '\u{1F1F5}\u{1F1EA}' },
  { code: '+52', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+53', flag: '\u{1F1E8}\u{1F1FA}' },
  { code: '+54', flag: '\u{1F1E6}\u{1F1F7}' },
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '+56', flag: '\u{1F1E8}\u{1F1F1}' },
  { code: '+57', flag: '\u{1F1E8}\u{1F1F4}' },
  { code: '+58', flag: '\u{1F1FB}\u{1F1EA}' },
  { code: '+60', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+62', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: '+63', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: '+64', flag: '\u{1F1F3}\u{1F1FF}' },
  { code: '+65', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+66', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: '+81', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+82', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: '+84', flag: '\u{1F1FB}\u{1F1F3}' },
  { code: '+86', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+90', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+92', flag: '\u{1F1F5}\u{1F1F0}' },
  { code: '+93', flag: '\u{1F1E6}\u{1F1EB}' },
  { code: '+94', flag: '\u{1F1F1}\u{1F1F0}' },
  { code: '+95', flag: '\u{1F1F2}\u{1F1F2}' },
  { code: '+98', flag: '\u{1F1EE}\u{1F1F7}' },
  { code: '+212', flag: '\u{1F1F2}\u{1F1E6}' },
  { code: '+213', flag: '\u{1F1E9}\u{1F1FF}' },
  { code: '+216', flag: '\u{1F1F9}\u{1F1F3}' },
  { code: '+218', flag: '\u{1F1F1}\u{1F1FE}' },
  { code: '+220', flag: '\u{1F1EC}\u{1F1F2}' },
  { code: '+234', flag: '\u{1F1F3}\u{1F1EC}' },
  { code: '+351', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: '+352', flag: '\u{1F1F1}\u{1F1FA}' },
  { code: '+353', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: '+354', flag: '\u{1F1EE}\u{1F1F8}' },
  { code: '+355', flag: '\u{1F1E6}\u{1F1F1}' },
  { code: '+356', flag: '\u{1F1F2}\u{1F1F9}' },
  { code: '+357', flag: '\u{1F1E8}\u{1F1FE}' },
  { code: '+358', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: '+359', flag: '\u{1F1E7}\u{1F1EC}' },
  { code: '+370', flag: '\u{1F1F1}\u{1F1F9}' },
  { code: '+371', flag: '\u{1F1F1}\u{1F1FB}' },
  { code: '+372', flag: '\u{1F1EA}\u{1F1EA}' },
  { code: '+380', flag: '\u{1F1FA}\u{1F1E6}' },
  { code: '+381', flag: '\u{1F1F7}\u{1F1F8}' },
  { code: '+382', flag: '\u{1F1F2}\u{1F1EA}' },
  { code: '+385', flag: '\u{1F1ED}\u{1F1F7}' },
  { code: '+386', flag: '\u{1F1F8}\u{1F1EE}' },
  { code: '+387', flag: '\u{1F1E7}\u{1F1E6}' },
  { code: '+389', flag: '\u{1F1F2}\u{1F1F0}' },
  { code: '+420', flag: '\u{1F1E8}\u{1F1FF}' },
  { code: '+421', flag: '\u{1F1F8}\u{1F1F0}' },
  { code: '+966', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+972', flag: '\u{1F1EE}\u{1F1F1}' },
  { code: '+973', flag: '\u{1F1E7}\u{1F1ED}' },
  { code: '+974', flag: '\u{1F1F6}\u{1F1E6}' },
  { code: '+975', flag: '\u{1F1E7}\u{1F1F9}' },
  { code: '+976', flag: '\u{1F1F2}\u{1F1F3}' },
  { code: '+977', flag: '\u{1F1F3}\u{1F1F5}' },
];

// Helper to get country translation key from code
const getCountryKey = (code) => `countries.c${code.replace('+', '')}`;

const LoginForm = ({ onToggleMode, isLight }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Mobile-friendly default: stay logged in 7 days
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [twoFAChallenge, setTwoFAChallenge] = useState(null); // { challenge_token }
  const [twoFACode, setTwoFACode] = useState('');
  const { login, verify2fa } = useAuth();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setLoading(true);

    const result = await login(email, password, rememberMe);
    if (result.requires_2fa) {
      setTwoFAChallenge({ challenge_token: result.challenge_token });
    } else if (!result.success) {
      if (result.error === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true);
      } else {
        setError(result.i18nKey ? t(result.i18nKey) : result.error);
      }
    }
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const r = await verify2fa(twoFAChallenge.challenge_token, twoFACode.replace(/\s/g, ''));
    if (!r.success) {
      setError(r.error);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await axios.post('/auth/resend-verification', { email });
      setResendDone(true);
    } catch (err) {
      console.warn('Resend failed:', err.message);
    }
    setResendLoading(false);
  };

  return (
    <>
    <Card className={`w-full max-w-md mx-auto ${isLight ? 'bg-white border border-gray-200 shadow-lg' : ''}`}>
      <CardContent className="pt-4 pb-4 px-4">
        {error && (
          <Alert variant="destructive" className={`mb-4 ${isLight ? 'bg-red-50 text-red-800 border-red-300' : ''}`}
            style={isLight ? { backgroundColor: '#fef2f2', color: '#991b1b', borderColor: '#fca5a5' } : {}}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {emailNotVerified && (
          <Alert className="mb-4 bg-amber-50 border-amber-300 text-amber-800" data-testid="email-not-verified-alert">
            <AlertDescription>
              <p className="font-medium mb-3">{t('auth.emailNotVerified')}</p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendDone}
                  className={`inline-flex items-center justify-center px-5 h-9 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-yellow-400'}`}
                  style={{ borderRadius: '9999px' }}
                  data-testid="resend-verification-button"
                >
                  {resendDone ? t('auth.verificationSent') : t('auth.resendVerification')}
                </button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {twoFAChallenge ? (
          <form onSubmit={handle2FASubmit} className="space-y-3" data-testid="login-2fa-form">
            <div className="text-center space-y-1 mb-2">
              <p className="text-sm font-semibold" style={{color: isLight ? '#1e293b' : '#f1f5f9'}}>{t('auth.twoFactorPrompt')}</p>
              <p className="text-xs" style={{color: isLight ? '#64748b' : '#94a3b8'}}>{t('auth.twoFactorHint')}</p>
            </div>
            <Input
              autoFocus
              value={twoFACode}
              onChange={e => setTwoFACode(e.target.value)}
              maxLength={10}
              placeholder="123 456"
              className={`text-center font-mono text-xl tracking-widest ${isLight ? 'bg-white text-gray-900 border-gray-300' : 'bg-gray-800 text-gray-100 border-gray-600'}`}
              data-testid="login-2fa-code"
            />
            {/* Bestätigen + Abbrechen in einer Zeile, Bestätigen links, Abbrechen rechts */}
            <div className="flex items-center gap-2 pt-1">
              <Button
                type="submit"
                disabled={loading || twoFACode.length < 6}
                className="flex-1 h-10 bg-transparent border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black dark:border-yellow-400 dark:text-yellow-400 dark:hover:bg-yellow-400 dark:hover:text-black font-semibold rounded-lg"
                data-testid="login-2fa-submit"
              >
                {loading ? 'Prüfe…' : 'Bestätigen'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setTwoFAChallenge(null); setTwoFACode(''); setError(''); }}
                className="flex-1 h-10 rounded-lg"
                data-testid="login-2fa-cancel"
              >
                Abbrechen
              </Button>
            </div>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="email" className={isLight ? 'text-gray-700' : ''}>{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`h-9 px-4 text-base rounded-full ${isLight ? 'bg-gray-50 border-gray-300 text-black' : ''}`}
              style={{ borderRadius: '9999px' }}
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="password" className={isLight ? 'text-gray-700' : ''}>{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={`h-9 px-4 text-base rounded-full ${isLight ? 'bg-gray-50 border-gray-300 text-black' : ''}`}
              style={{ borderRadius: '9999px' }}
              data-testid="login-password-input"
            />
          </div>
          <button 
            type="submit" 
            className="w-full h-9 font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-200 login-submit-btn bg-yellow-500 text-black hover:bg-yellow-600 border-0"
            style={{ backgroundColor: isLight ? '#2563eb' : '#eab308', color: isLight ? 'white' : 'black', border: 'none', borderRadius: '9999px' }}
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>
        )}
      </CardContent>
    </Card>
    {/* "Angemeldet bleiben" — bewusst außerhalb der Card, unterhalb des Login-Formulars */}
    {!twoFAChallenge && (
      <div className="flex items-center justify-center gap-2 mt-3 w-full max-w-md mx-auto">
        <input
          id="remember-me"
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="cursor-pointer"
          data-testid="remember-me-checkbox"
        />
        <label htmlFor="remember-me" className={`text-xs cursor-pointer ${isLight ? 'text-gray-600' : 'text-gray-400'}`} data-testid="remember-me-label">
          {t('auth.rememberMe')}
        </label>
      </div>
    )}
    </>
  );
};

const RegisterForm = ({ onToggleMode, isLight }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_country_code: '+49',
    phone_number: '',
    phone: '',
    address: '',
    street: '',
    postal_code: '',
    city: '',
    tax_number: '',
    hourly_rate: 60.0,
    profession_type: ''
  });
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const { register } = useAuth();
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: name === 'hourly_rate' ? parseFloat(value) : value
      };
      
      // Combine phone fields
      if (name === 'phone_country_code' || name === 'phone_number') {
        updated.phone = `${updated.phone_country_code}${updated.phone_number}`;
      }
      
      // Combine address fields into legacy address field for backward compatibility
      if (name === 'street' || name === 'postal_code' || name === 'city') {
        updated.address = `${updated.street || ''}, ${updated.postal_code || ''} ${updated.city || ''}`.trim();
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate consent checkbox
    if (!consentAccepted) {
      setError(t('auth.consentRequired'));
      return;
    }

    setLoading(true);

    const result = await register(formData);
    if (!result.success) {
      setError(result.i18nKey ? t(result.i18nKey) : result.error);
    } else if (result.needsVerification) {
      setRegistrationComplete(true);
    }
    setLoading(false);
  };

  return (
    <>
    {registrationComplete ? (
      <Card className={`w-full max-w-md mx-auto ${isLight ? 'bg-white border border-gray-200 shadow-lg' : ''}`}>
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <h2 className={`text-xl font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>
            {t('auth.checkEmail')}
          </h2>
          <p className={`text-lg font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>
            {formData.email}
          </p>
          <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>
            {t('auth.checkEmailText').replace('{email}', '').replace('  ', ' ').trim()}
          </p>
          <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
            {t('auth.checkSpam')}
          </p>
          <button
            onClick={onToggleMode}
            className={isLight ? 'px-6 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors' : 'px-6 py-2 rounded-full bg-gray-700 hover:bg-gray-600 text-yellow-400 font-medium transition-colors'}
            data-testid="back-to-login-button"
          >
            {t('auth.hasAccount')}
          </button>
        </CardContent>
      </Card>
    ) : (
    <>
    <Card className={`w-full max-w-md mx-auto ${isLight ? 'bg-white border border-gray-200 shadow-lg' : ''}`}>
      <CardHeader>
        <CardTitle className={`text-center ${isLight ? 'text-gray-900' : ''}`}>{t('auth.register')}</CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className={`mb-4 ${isLight ? 'bg-red-50 text-red-800 border-red-300' : ''}`}
            style={isLight ? { backgroundColor: '#fef2f2', color: '#991b1b', borderColor: '#fca5a5' } : {}}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-2.5 register-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="first_name">{t('auth.firstName')}</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                data-testid="register-first-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="last_name">{t('auth.lastName')}</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                data-testid="register-last-name-input"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="register-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              data-testid="register-password-input"
            />
            <p className="text-xs text-gray-500">{t('auth.passwordRules')}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="phone_number">{t('auth.phoneNumber')}</Label>
            <div className="flex gap-2">
              <div className="relative w-24">
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="country-code-selector w-full px-2 h-[2.25rem] border-0 rounded-lg text-sm text-left flex items-center justify-center gap-1"
                >
                  <span>{countryCodes.find(c => c.code === formData.phone_country_code)?.flag}</span>
                  <span className="font-medium">{formData.phone_country_code}</span>
                </button>
                {showCountryDropdown && (
                  <div className="absolute z-50 w-72 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                    <input
                      type="text"
                      placeholder={t('auth.searchCountryCode')}
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                      autoFocus
                      className="w-full px-3 py-2 border-b border-gray-300 bg-white text-black text-sm focus:outline-none"
                    />
                    <div className="max-h-60 overflow-y-auto bg-white">
                      {countryCodes
                        .filter(country => {
                          const translatedName = t(getCountryKey(country.code));
                          return translatedName.toLowerCase().includes(countrySearch.toLowerCase()) ||
                            country.code.includes(countrySearch);
                        })
                        .map(country => (
                          <div
                            key={country.code}
                            onClick={() => {
                              handleChange({ target: { name: 'phone_country_code', value: country.code } });
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                            }}
                            className="px-3 py-2 bg-white hover:bg-gray-100 cursor-pointer text-black text-sm flex items-center gap-2"
                          >
                            <span>{country.flag}</span>
                            <span className="font-medium text-black">{country.code}</span>
                            <span className="text-gray-600">{t(getCountryKey(country.code))}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>
              <Input
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="170 1234567"
                className="flex-1 bg-white border-gray-300 text-black"
                data-testid="register-phone-input"
              />
            </div>
          </div>
          
          {/* Adresse - 3 logische Felder */}
          <div className="space-y-2">
            <Label htmlFor="street">{t('auth.street')}</Label>
            <Input
              id="street"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder={t('auth.streetPlaceholder')}
              className="bg-white border-gray-300 text-black"
              data-testid="register-street-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="postal_code">{t('auth.postalCode')}</Label>
              <Input
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="12345"
                className="bg-white border-gray-300 text-black"
                data-testid="register-postal-code-input"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="city">{t('auth.city')}</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={t('auth.cityPlaceholder')}
                className="bg-white border-gray-300 text-black"
                data-testid="register-city-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tax_number">{t('auth.taxNumber')}</Label>
            <Input
              id="tax_number"
              name="tax_number"
              value={formData.tax_number}
              onChange={handleChange}
              data-testid="register-tax-number-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hourly_rate">{t('auth.hourlyRate')}</Label>
            <Input
              id="hourly_rate"
              name="hourly_rate"
              type="number"
              step="0.01"
              value={formData.hourly_rate}
              onChange={handleChange}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value)) {
                  setFormData(prev => ({ ...prev, hourly_rate: roundHourlyRate(value) }));
                }
              }}
              required
              className="text-right"
              data-testid="register-hourly-rate-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profession_type">{t('auth.professionType')}</Label>
            <IOSelect
              id="profession_type"
              name="profession_type"
              value={formData.profession_type}
              onChange={handleChange}
              className="w-full h-10 text-sm rounded-md bg-white text-gray-900 border border-gray-300 px-3"
              data-testid="register-profession-type"
            >
              <option value="">{t('auth.selectProfession')}</option>
              <option value="gebaerdensprachdolmetscher">{t('auth.professions.signLanguageInterpreter')}</option>
              <option value="dolmetscher">{t('auth.professions.interpreter')}</option>
              <option value="kommunikationsassistent">{t('auth.professions.communicationAssistant')}</option>
              <option value="uebersetzer">{t('auth.professions.translator')}</option>
              <option value="schriftdolmetscher">{t('auth.professions.stenotypist')}</option>
              <option value="taubblindenassistent">{t('auth.professions.deafblindAssistant')}</option>
            </IOSelect>
          </div>
          
          {/* DSGVO Consent Checkbox */}
          <div className="flex items-start space-x-3 pt-2">
            <div 
              className="mt-1 flex-shrink-0 cursor-pointer"
              onClick={() => setConsentAccepted(!consentAccepted)}
              role="checkbox"
              aria-checked={consentAccepted}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setConsentAccepted(!consentAccepted); } }}
              data-testid="register-consent-checkbox"
            >
              <div 
                className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                  consentAccepted 
                    ? 'bg-blue-600 border-blue-600 dark:bg-yellow-400 dark:border-yellow-400' 
                    : 'bg-transparent border-gray-400 dark:border-gray-500'
                }`}
              >
                {consentAccepted && (
                  <svg className="w-3.5 h-3.5" style={{ color: '#ffffff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <Label className="text-sm font-normal leading-tight cursor-pointer text-justify" onClick={() => setConsentAccepted(!consentAccepted)}>
              {t('auth.consentPrefix')}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.preventDefault(); setShowDatenschutz(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter') setShowDatenschutz(true); }}
                className="underline cursor-pointer hover:opacity-80"
                style={{ background: 'transparent', color: '#f59e0b', textDecoration: 'underline' }}
                data-testid="register-privacy-link"
              >
                {t('auth.privacyPolicy')}
              </span>
              {t('auth.consentSuffix')}
            </Label>
          </div>
          
          <div className="pt-1">
          <Button 
            type="submit" 
            className={`w-full h-10 rounded-full font-semibold border-2 ${isLight ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' : 'bg-gray-900 hover:bg-gray-800 text-yellow-400 border-yellow-400'}`}
            style={{ borderRadius: '9999px' }}
            disabled={loading || !consentAccepted}
            data-testid="register-submit-button"
          >
            {loading ? t('auth.registering') : t('auth.register')}
          </Button>
          </div>
        </form>
        <div className="mt-4 text-center">
          <button
            onClick={onToggleMode}
            className={isLight ? 'px-6 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors' : 'px-6 py-2 rounded-full bg-gray-700 hover:bg-gray-600 text-yellow-400 text-sm font-medium transition-colors'}
            data-testid="toggle-to-login-button"
          >
            {t('auth.hasAccount')}
          </button>
        </div>
      </CardContent>
    </Card>

    {showDatenschutz && (
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
          <Suspense fallback={<div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400"></div></div>}>
            <DatenschutzPage onBack={() => setShowDatenschutz(false)} />
          </Suspense>
        </div>
      </div>
    )}
    </>
    )}
    </>
  );
};

const AuthPage = ({ onOpenLanding, authRequest } = {}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState(null); // 'success' | 'error' | null
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [reportResetStatus, setReportResetStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    if (!authRequest) return;
    setIsLogin(authRequest.mode !== 'register');
    setShowPasswordReset(false);
  }, [authRequest]);

  // Handle email verification, password reset tokens, and unauthorized reset reports from URL
  const verifyAttempted = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('verify');
    const resetToken = params.get('reset-token');
    const reportResetToken = params.get('report-reset');
    const modeParam = params.get('mode');

    // Landing-Page linkt mit `?mode=register` — sofort das Register-Formular öffnen
    if (modeParam === 'register') {
      setIsLogin(false);
    }

    if (verifyToken && !verifyAttempted.current) {
      verifyAttempted.current = true;
      axios.get(`/auth/verify-email?token=${verifyToken}`)
        .then(() => {
          setVerifyStatus('success');
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch(() => {
          setVerifyStatus('error');
          window.history.replaceState({}, '', window.location.pathname);
        });
    }

    if (resetToken) {
      setShowPasswordReset(true);
    }

    if (reportResetToken && !verifyAttempted.current) {
      verifyAttempted.current = true;
      axios.get(`/auth/report-unauthorized-reset?token=${reportResetToken}`)
        .then(() => {
          setReportResetStatus('success');
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch(() => {
          setReportResetStatus('error');
          window.history.replaceState({}, '', window.location.pathname);
        });
    }
  }, []);

  const bgClass = isLight
    ? 'bg-white'
    : 'bg-gradient-to-br from-gray-900 via-gray-800 to-black';
  const toggleBtnClass = isLight
    ? 'inline-flex items-center justify-center px-[22px] h-9 !rounded-full bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors'
    : 'inline-flex items-center justify-center px-6 h-9 !rounded-full bg-gray-700 hover:bg-gray-600 text-yellow-400 font-medium transition-colors';

  if (showPasswordReset) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-start lg:items-center justify-center pt-6 sm:pt-8 lg:py-12 pb-6 px-4 sm:px-6 lg:px-8`}>
        <ThemeOrb />
        <Suspense fallback={<div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400"></div></div>}>
          <PasswordReset onBackToLogin={() => setShowPasswordReset(false)} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgClass} flex items-start justify-center pt-28 sm:pt-32 pb-6 px-4 sm:px-6 lg:px-8`}>
      <LanguageOrb />
      <ThemeOrb />
      <MoreInfoLink onOpenLanding={onOpenLanding} />
      <div className="w-full max-w-sm space-y-4">
        {/* Logo + Wortmarke — Platzhalter für SharedBrand (siehe PublicShell) */}
        <div className="flex flex-col items-center" data-testid="auth-brand-placeholder">
          <div className="w-28 h-28 rounded-full flex items-center justify-center mb-2 invisible"
            style={{ 
              backgroundColor: '#facc15',
            }}>
            <span style={{ 
              fontSize: '56px', 
              fontWeight: 700, 
              color: '#1a1a1a', 
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: '1',
              letterSpacing: '-0.02em'
            }}>IO</span>
          </div>
          <h1 className={`text-3xl font-bold invisible mb-6 sm:mb-4 ${isLight ? 'text-black' : 'bg-gradient-to-r from-yellow-400 to-[#da9e06] bg-clip-text text-transparent'}`}>
            Interpreter's Office
          </h1>
          <p className={`text-sm text-center ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
            Verwalte deine Einsätze und Abrechnungen<br />professionell
          </p>
        </div>
        {reportResetStatus === 'success' && (
          <Alert className="bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300">
            <AlertDescription>
              {t('auth.reportResetSuccess')}
            </AlertDescription>
          </Alert>
        )}
        {reportResetStatus === 'error' && (
          <Alert className="bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300">
            <AlertDescription>
              {t('auth.reportResetAlready')}
            </AlertDescription>
          </Alert>
        )}
        {verifyStatus === 'success' && (
          <Alert className="bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300">
            <AlertDescription>{t('auth.verificationSuccess')}</AlertDescription>
          </Alert>
        )}
        {verifyStatus === 'error' && (
          <Alert variant="destructive">
            <AlertDescription>{t('auth.verificationFailed')}</AlertDescription>
          </Alert>
        )}
        {isLogin ? (
          <>
            <LoginForm isLight={isLight} onToggleMode={() => setIsLogin(false)} />
            <div className="text-center -mt-3">
              <button
                onClick={() => setIsLogin(false)}
                className={toggleBtnClass}
                data-testid="toggle-to-register-button"
              >
                {t('auth.noAccount')}
              </button>
            </div>
            <div className="text-center -mt-2">
              <button
                onClick={() => setShowPasswordReset(true)}
                className={toggleBtnClass}
                data-testid="forgot-password-link"
              >
                {t('auth.forgotPassword')}
              </button>
            </div>
          </>
        ) : (
          <RegisterForm isLight={isLight} onToggleMode={() => setIsLogin(true)} />
        )}
      </div>
    </div>
  );
};

export default AuthPage;